// File: components/Dashboard.tsx
import React, { useState, useEffect, useRef } from 'react';
import { PlayerProfile, IdolRank } from '../types';
import { RANKS } from '../constants';
import { DollarSign, Users, Music, Heart, Landmark, Crown } from 'lucide-react';

import { NotificationSystem, Notification } from './NotificationSystem';

interface DashboardProps {
  player: PlayerProfile;
  bobbyEarnings: number;
  notifications: Notification[];
  children?: React.ReactNode; // Slot for Music Player / Tools
}

export const Dashboard: React.FC<DashboardProps> = ({ player, bobbyEarnings, notifications, children }) => {
  const rankData = RANKS[player.rank] || RANKS[IdolRank.TRAINEE];
  const [displayedCash, setDisplayedCash] = useState(player.cash);
  // FIX: Corrected the type for useRef. The ref is initialized without a value, so its type must include 'undefined'.
  const animationFrameRef = useRef<number | undefined>(undefined);

  // FIX: The animation termination logic was causing an error in test environments by calling
  // cancelAnimationFrame without an argument. The correct way to stop the loop is to simply
  // not request a new animation frame when the target value is reached. The invalid call
  // has been removed.
  useEffect(() => {
    const endValue = player.cash;

    const animate = () => {
      setDisplayedCash(currentDisplayed => {
        if (Math.abs(endValue - currentDisplayed) < 1) {
          // The animation loop stops on its own by not requesting a new frame.
          return endValue;
        }
        // Smoothly move towards the target value
        const nextValue = currentDisplayed + (endValue - currentDisplayed) * 0.1;
        animationFrameRef.current = requestAnimationFrame(animate);
        return nextValue;
      });
    };

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [player.cash]);


  return (
    <header className="relative w-full bg-slate-950 border-b border-slate-800 z-[200] flex flex-col md:flex-row items-center gap-2 p-2 md:px-4 shrink-0 shadow-xl">
      <NotificationSystem notifications={notifications} />

      {/* Top Row (Mobile): Identity & Key Resources */}
      <div className="w-full md:w-auto flex items-center justify-between gap-4 overflow-x-auto no-scrollbar">

        {/* Identity */}
        <div className="flex items-center gap-3 shrink-0">
          <div className="w-8 h-8 md:w-10 md:h-10 border border-slate-700 bg-slate-800 flex items-center justify-center rounded-lg">
            <Music size={16} className="text-purple-400" />
          </div>
          <div>
            <div className="font-heading text-slate-100 text-sm md:text-base leading-none">{player.name}</div>
            <div className={`text-[10px] font-bold ${rankData.color} flex items-center gap-1 mt-0.5`}>
              <Crown size={10} /> {rankData.label}
            </div>
          </div>
        </div>

        {/* Separator */}
        <div className="h-6 w-px bg-slate-800 hidden md:block"></div>

        {/* Stats Grid */}
        <div className="flex gap-3 md:gap-6 shrink-0">
          <div className="flex flex-col items-end md:items-start">
            <div className="flex items-center gap-1 text-[10px] text-slate-500 font-bold uppercase"><Users size={10} /> Fans</div>
            <div className="text-slate-200 text-sm font-mono font-bold">{((player?.fans || 0) / 1000).toFixed(1)}k</div>
          </div>

          <div className="flex flex-col items-end md:items-start">
            <div className="flex items-center gap-1 text-[10px] text-slate-500 font-bold uppercase"><Heart size={10} /> Wards</div>
            <div className="text-slate-200 text-sm font-mono font-bold">{player?.mysticWards || 0}</div>
          </div>

          <div className="bg-slate-800/50 px-2 py-1 rounded border border-slate-700 flex items-center gap-2">
            <DollarSign size={14} className="text-green-400" />
            <span className="text-slate-100 text-sm font-mono font-bold">{Math.floor(displayedCash || 0).toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Children Container (Music Player & Tools) */}
      <div className="w-full md:w-auto md:ml-auto flex items-center justify-center md:justify-end gap-2 border-t md:border-t-0 border-slate-800 pt-2 md:pt-0">
        {children}
      </div>

    </header>
  );
};